/**
 * Conversation Storage Manager
 * Handles localStorage persistence for chat conversations with expiration
 */

import { Message, FlowStep } from './types';

const STORAGE_KEY = 'dreambot_conversation';
const EXPIRATION_HOURS = 48; // 48 hours conversation retention

export interface StoredConversation {
  messages: Message[];
  flowStep: FlowStep;
  userPrefs: Record<string, any>;
  timestamp: number;
  sessionId: string;
}

/**
 * Save conversation to localStorage
 */
export function saveConversation(
  messages: Message[],
  flowStep: FlowStep,
  userPrefs: Record<string, any>,
  sessionId: string
): void {
  try {
    const conversation: StoredConversation = {
      messages,
      flowStep,
      userPrefs,
      timestamp: Date.now(),
      sessionId,
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversation));
  } catch (error) {
    // Silently fail - not critical
  }
}

/**
 * Load conversation from localStorage
 * Returns null if expired or not found
 */
export function loadConversation(): StoredConversation | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const conversation: StoredConversation = JSON.parse(stored);
    
    // Check if expired
    const ageInHours = (Date.now() - conversation.timestamp) / (1000 * 60 * 60);
    if (ageInHours > EXPIRATION_HOURS) {
      clearConversation();
      return null;
    }

    // Validate structure
    if (!conversation.messages || !Array.isArray(conversation.messages)) {
      return null;
    }

    return conversation;
  } catch (error) {
    return null;
  }
}

/**
 * Clear conversation from localStorage
 */
export function clearConversation(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Silently fail
  }
}

/**
 * Check if a valid conversation exists
 */
export function hasStoredConversation(): boolean {
  return loadConversation() !== null;
}

/**
 * Get conversation age in hours
 */
export function getConversationAge(): number | null {
  const conversation = loadConversation();
  if (!conversation) return null;
  
  return (Date.now() - conversation.timestamp) / (1000 * 60 * 60);
}

/**
 * Format conversation age for display
 */
export function formatConversationAge(hours: number): string {
  if (hours < 1) {
    const minutes = Math.floor(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }
  
  if (hours < 24) {
    const roundedHours = Math.floor(hours);
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''} ago`;
  }
  
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}
